/**
 * Populations — provenance, reconciliation, samples.
 *   T6-S2  reconciliation needs an independent control total (ReconcileForm).
 *   T6-B5  no source yet → "Waiting on the client to record the source" and
 *          "Remind client" (a PBC request / message the client sees).
 *   T6-B6  each sample shows its items, seed and lineage; once an original
 *          exists, Draw gives way to Extend… / Redraw… (rationale required);
 *          an unreconciled population shows Draw unavailable: "Reconcile first".
 */
import { useState, type FormEvent } from 'react';
import { Alert, EmptyState, Field, Input, Textarea } from '@aigovops/design-system';
import { firmApi, type FirmPopulation, type FirmSample, type PbcRequest } from '../../lib/firm-api';
import { fmtDate, plural } from '../../lib/format';
import { Chip, GuardedButton, TabHeading, useAct, useDraft, useFirm, useFocusLater, useLoad, Loading } from './firm-kit';

const KIND = (k: string) => k.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
const SAMPLE_KIND: Record<FirmSample['kind'], string> = { original: 'Original draw', extension: 'Extension', redraw: 'Redraw', imported: 'Imported selection' };

export function PopulationsTab() {
  const { slug, eng } = useFirm();
  const l = useLoad(() => firmApi.populations(slug, eng), [slug, eng]);
  const pbc = useLoad(() => firmApi.pbc(slug, eng), [slug, eng]);
  return (
    <div className="gp-firm-stack">
      <TabHeading id="pop-h">Populations</TabHeading>
      <Loading l={l}>{(d) => d.populations.length === 0
        ? <EmptyState title="No populations for this period" action={<a href="?tab=pbc">Request one</a>} />
        : (
          <ul className="gp-firm-list" aria-labelledby="pop-h">
            {d.populations.map((p) => <PopulationRow key={p.population_id} p={p} requests={pbc.data?.requests ?? []} onChanged={() => { l.reload(); pbc.reload(); }} />)}
          </ul>
        )}</Loading>
    </div>
  );
}

function PopulationRow({ p, requests, onChanged }: { p: FirmPopulation; requests: PbcRequest[]; onChanged: () => void }) {
  const { slug, eng, writable } = useFirm();
  const act = useAct(onChanged);
  const reconciled = !!p.reconciliation?.current;
  const original = p.samples.find((s) => s.kind === 'original' || s.kind === 'imported');
  const latest = p.samples[p.samples.length - 1];
  const remindTitle = `Record where the ${KIND(p.kind).toLowerCase()} population came from`;
  const open = requests.find((r) => r.title === remindTitle && (r.status === 'requested' || r.status === 'returned'));

  const remind = () => act.run(
    () => (open
      ? firmApi.pbcMessage(slug, eng, open.request_id, 'Reminder: please record the source system and how this population was extracted.')
      : firmApi.createPbc(slug, eng, { title: remindTitle, detail: 'Record the source system, how the list was extracted, when, and whether it was adjusted.' })),
    open ? `Reminder sent on ${open.ref}` : 'Request sent to the client',
  );

  return (
    <li className="gp-firm-row" id={`pop-${p.population_id}`}>
      <div className="gp-firm-row-head">
        <h3 className="gp-firm-row-title">{KIND(p.kind)} · {plural(p.item_count, 'item')}</h3>
        {reconciled ? <Chip tone="success">Reconciled</Chip> : <Chip tone={p.provenance ? 'accent' : 'neutral'}>{p.provenance ? 'Reconcile' : 'Waiting on source'}</Chip>}
      </div>
      {act.error ? <Alert variant="error">{act.error}</Alert> : null}
      {p.provenance ? (
        <p className="gp-firm-muted">{p.provenance.source_system} · {p.provenance.extraction_method.replace(/_/g, ' ')} · extracted {fmtDate(p.provenance.extracted_at)} · <strong>{p.provenance.independence_label}</strong></p>
      ) : (
        <div className="gp-firm-actions">
          <span className="gp-firm-muted">Waiting on the client to record the source</span>
          {writable ? <GuardedButton compact busy={act.busy} onClick={() => void remind()}>{open ? `Remind client (${open.ref})` : 'Remind client'}</GuardedButton> : null}
        </div>
      )}
      {p.reconciliation ? (
        <dl className="gp-firm-dl">
          <dt>Control total</dt>
          <dd>{p.reconciliation.control_total}{p.reconciliation.control_total_source ? ` from ${p.reconciliation.control_total_source}` : ''} · {p.reconciliation.agrees ? 'agrees' : `differs by ${Math.abs(p.reconciliation.control_total - p.item_count)}`}</dd>
          {p.reconciliation.difference_explanation ? <><dt>Difference</dt><dd>{p.reconciliation.difference_explanation}</dd></> : null}
          <dt>Memo</dt>
          <dd>{p.reconciliation.memo}</dd>
          <dt>Signed</dt>
          <dd>{p.reconciliation.signed_by_name} · {fmtDate(p.reconciliation.signed_at)}</dd>
        </dl>
      ) : null}
      {writable && p.provenance && !reconciled ? (
        <ReconcileForm population={p} busy={act.busy} onSubmit={async (body) => { await act.call('reconcile', [slug, eng, p.population_id, body], `${KIND(p.kind)} reconciled`); }} />
      ) : null}
      {p.samples.length ? (
        <ul className="gp-firm-samples" aria-label={`Samples of ${KIND(p.kind).toLowerCase()}`}>
          {p.samples.map((s) => <SampleRow key={s.lineage_id} s={s} parent={p.samples.find((x) => x.lineage_id === s.parent_lineage_id) ?? null} onChanged={onChanged} />)}
        </ul>
      ) : null}
      {writable ? (
        original && latest ? <ExtendRedraw p={p} from={latest} reconciled={reconciled} onChanged={onChanged} /> : <DrawForm p={p} reconciled={reconciled} onChanged={onChanged} />
      ) : null}
    </li>
  );
}

function SampleRow({ s, parent, onChanged }: { s: FirmSample; parent: FirmSample | null; onChanged: () => void }) {
  const { slug, eng, writable } = useFirm();
  const act = useAct(onChanged);
  return (
    <li className="gp-firm-sample" id={`smp-${s.lineage_id}`}>
      <p className="gp-firm-sample-head">
        <strong>{SAMPLE_KIND[s.kind]}</strong> · {plural(s.sample_size, 'item')}
        {s.seed ? <> · seed <code>{s.seed}</code></> : null}
        {parent ? <> · from {SAMPLE_KIND[parent.kind].toLowerCase()} of {fmtDate(parent.created_at)}</> : null}
        {' · '}{fmtDate(s.created_at)}
      </p>
      {s.rationale ? <p className="gp-firm-muted">Why: {s.rationale}</p> : null}
      <ul className="gp-firm-keys" aria-label="Selected items">{s.selected_keys.map((k) => <li key={k}>{k}</li>)}</ul>
      {act.error ? <Alert variant="error">{act.error}</Alert> : null}
      {s.requested_at ? <p className="gp-firm-muted">Sent to the client {fmtDate(s.requested_at)}</p> : writable ? (
        <div className="gp-firm-actions"><GuardedButton compact busy={act.busy} onClick={() => void act.call('requestSelection', [slug, eng, s.lineage_id], 'Selection sent to the client')}>Send to client</GuardedButton></div>
      ) : null}
    </li>
  );
}

function DrawForm({ p, reconciled, onChanged }: { p: FirmPopulation; reconciled: boolean; onChanged: () => void }) {
  const { slug, eng } = useFirm();
  const act = useAct(onChanged);
  const [size, setSize] = useDraft(`draw-${p.population_id}`, '');
  const [tried, setTried] = useState(false);
  const n = /^\d+$/.test(size.trim()) ? Number(size.trim()) : 0;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!reconciled) return;
    setTried(true);
    if (n < 1 || n > p.item_count) return;
    if (await act.call('draw', [slug, eng, p.population_id, { sample_size: n }], `Sample of ${plural(n, 'item')} drawn`)) { setSize(''); setTried(false); }
  };
  return (
    <form className="gp-firm-form gp-firm-form--row" onSubmit={submit} noValidate>
      {act.error ? <Alert variant="error">{act.error}</Alert> : null}
      <Field id={`draw-size-${p.population_id}`} label="Sample size" hint={`1–${p.item_count}`} error={tried && (n < 1 || n > p.item_count) ? `Enter a size from 1 to ${p.item_count}.` : null}>
        <Input inputMode="numeric" value={size} onChange={(e) => setSize(e.target.value)} />
      </Field>
      <div className="gp-firm-actions">
        <GuardedButton type="submit" busy={act.busy} blocked={!reconciled} reason="Reconcile first">Draw sample</GuardedButton>
      </div>
    </form>
  );
}

function ExtendRedraw({ p, from, reconciled, onChanged }: { p: FirmPopulation; from: FirmSample; reconciled: boolean; onChanged: () => void }) {
  const { slug, eng } = useFirm();
  const act = useAct(onChanged);
  const focus = useFocusLater();
  const [mode, setMode] = useState<'extend' | 'redraw' | null>(null);
  const [count, setCount] = useDraft(`xr-n-${p.population_id}`, '');
  const [why, setWhy] = useDraft(`xr-why-${p.population_id}`, '');
  const [tried, setTried] = useState(false);
  const n = /^\d+$/.test(count.trim()) ? Number(count.trim()) : 0;
  const max = mode === 'extend' ? p.item_count - from.sample_size : p.item_count;
  const errs = { n: n < 1 || n > max ? `Enter a number from 1 to ${max}.` : null, why: why.trim().length < 10 ? 'Give the rationale (at least 10 characters).' : null };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (!mode || errs.n || errs.why) return;
    const ok = mode === 'extend'
      ? await act.call('extend', [slug, eng, from.lineage_id, { additional: n, rationale: why.trim() }], `Sample extended by ${plural(n, 'item')}`)
      : await act.call('redraw', [slug, eng, from.lineage_id, { sample_size: n, rationale: why.trim() }], `Sample redrawn: ${plural(n, 'item')}`);
    if (ok) { setMode(null); setCount(''); setWhy(''); setTried(false); focus(`pop-${p.population_id}`); }
  };
  if (!reconciled) return <div className="gp-firm-actions"><GuardedButton blocked reason="Reconcile first">Extend…</GuardedButton></div>;
  if (!mode) {
    return (
      <div className="gp-firm-actions">
        <GuardedButton compact onClick={() => { setMode('extend'); focus(`xr-n-f-${p.population_id}`); }}>Extend…</GuardedButton>
        <GuardedButton compact onClick={() => { setMode('redraw'); focus(`xr-n-f-${p.population_id}`); }}>Redraw…</GuardedButton>
      </div>
    );
  }
  return (
    <form className="gp-firm-form" onSubmit={submit} noValidate aria-label={mode === 'extend' ? 'Extend the sample' : 'Redraw the sample'}>
      {act.error ? <Alert variant="error">{act.error}</Alert> : null}
      <Field id={`xr-n-f-${p.population_id}`} label={mode === 'extend' ? `Add how many items to the ${plural(from.sample_size, 'item')}?` : 'New sample size'} error={tried ? errs.n : null}>
        <Input inputMode="numeric" value={count} onChange={(e) => setCount(e.target.value)} />
      </Field>
      <Field id={`xr-why-f-${p.population_id}`} label="Rationale" error={tried ? errs.why : null}>
        <Textarea rows={2} value={why} onChange={(e) => setWhy(e.target.value)} />
      </Field>
      <div className="gp-firm-actions">
        <GuardedButton type="submit" busy={act.busy}>{mode === 'extend' ? 'Extend sample' : 'Redraw sample'}</GuardedButton>
        <GuardedButton variant="ghost" onClick={() => { setMode(null); setTried(false); }}>Cancel</GuardedButton>
      </div>
    </form>
  );
}

/**
 * T6-S2: reconciliation needs an INDEPENDENT control total — typed in, with
 * its source named — never the population's own count. The difference shows
 * live; sign-off stays unavailable until the totals agree or the difference
 * is explained (the server enforces the same rules).
 */
export function ReconcileForm({ population: p, busy, onSubmit }: {
  population: FirmPopulation;
  busy: boolean;
  onSubmit: (body: { control_total: number; control_total_source: string; difference_explanation: string | null; memo: string }) => Promise<void>;
}) {
  const id = p.population_id;
  const [total, setTotal] = useState('');
  const [source, setSource] = useState('');
  const [why, setWhy] = useState('');
  const [memo, setMemo] = useState('');
  const [tried, setTried] = useState(false);
  const n = /^\d+$/.test(total.trim()) ? Number(total.trim()) : null;
  const diff = n === null ? null : n - p.item_count;
  const errors = {
    total: n === null ? 'Enter the total from the independent source.' : null,
    source: source.trim().length < 3 ? 'Name the source of the control total.' : null,
    why: diff !== null && diff !== 0 && why.trim().length < 20 ? 'Explain the difference (at least 20 characters).' : null,
    memo: memo.trim().length < 10 ? 'Write the memo (at least 10 characters).' : null,
  };
  const blocked = Object.values(errors).some(Boolean);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (blocked || busy || n === null) return;
    void onSubmit({ control_total: n, control_total_source: source.trim(), difference_explanation: diff ? why.trim() : null, memo: memo.trim() });
  };
  return (
    <form className="gp-firm-form" onSubmit={submit} noValidate aria-label={`Reconcile ${p.kind.replace(/_/g, ' ')}`}>
      <Field id={`rc-total-${id}`} label="Control total (independent source)" hint={`Population: ${p.item_count} items`} error={tried ? errors.total : null}>
        <Input inputMode="numeric" value={total} onChange={(e) => setTotal(e.target.value)} autoComplete="off" />
      </Field>
      <Field id={`rc-source-${id}`} label="Source of the control total" hint="e.g. Workday headcount roll-forward" error={tried ? errors.source : null}>
        <Input value={source} onChange={(e) => setSource(e.target.value)} />
      </Field>
      <p className="gp-firm-muted" aria-live="polite" id={`rc-diff-${id}`}>
        {diff === null ? 'Difference shows once you enter the total.' : diff === 0
          ? <span className="gp-firm-ok">Agrees with the population ({p.item_count})</span>
          : <span className="gp-firm-lock">Differs by {Math.abs(diff)} ({n} vs {p.item_count})</span>}
      </p>
      {diff ? (
        <Field id={`rc-why-${id}`} label="Explain the difference" error={tried ? errors.why : null}>
          <Textarea rows={2} value={why} onChange={(e) => setWhy(e.target.value)} />
        </Field>
      ) : null}
      <Field id={`rc-memo-${id}`} label="Reconciliation memo" error={tried ? errors.memo : null}>
        <Textarea rows={2} value={memo} onChange={(e) => setMemo(e.target.value)} />
      </Field>
      <div className="gp-firm-actions">
        <GuardedButton type="submit" busy={busy} blocked={blocked} reasonId={`rc-rule-${id}`} reason="Needs an independent total and its source, a memo, and an explanation if the totals differ.">Sign off reconciliation</GuardedButton>
      </div>
    </form>
  );
}
